// Mini demos modal — opened from the onboarding welcome screen's
// "Watch mini demos" option. Each card starts a real in-app guided tour
// (see `TourController`). No video files, no mock screens.

import { Play, Clock, Compass } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { MINI_DEMOS, type MiniDemo } from "@/lib/onboarding/miniDemos";
import { startTour } from "@/lib/onboarding/tour-state";

export function MiniDemosSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 text-left">
          <DialogTitle className="text-lg">Mini demos</DialogTitle>
          <DialogDescription className="text-sm">
            Short guided tours in the real app — Roava highlights the next
            control and you tap to continue. Nothing is changed for you.
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 pb-5 max-h-[75vh] overflow-y-auto space-y-2">
          {MINI_DEMOS.map((d) => (
            <DemoCard key={d.id} demo={d} onStart={() => {
              onOpenChange(false);
              // Slight delay so the dialog finishes closing before the
              // spotlight measures targets.
              setTimeout(() => startTour(d.tourId), 250);
            }} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DemoCard({ demo, onStart }: { demo: MiniDemo; onStart: () => void }) {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex gap-3 p-3">
        <div className="relative size-16 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-primary/15 via-accent to-primary/5 grid place-items-center">
          <Compass className="size-6 text-muted-foreground/70" aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{demo.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
            {demo.description}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              <Clock className="size-3" aria-hidden />
              {demo.durationLabel}
            </span>
            <button
              type="button"
              onClick={onStart}
              aria-label={`Start ${demo.title} tour`}
              className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-[11px] font-semibold hover:opacity-90 transition"
            >
              <Play className="size-3" aria-hidden />
              Start tour
            </button>
          </div>
        </div>
      </div>
      {(demo.whyItMatters || demo.bestFor || (demo.affects && demo.affects.length > 0)) && (
        <div className="border-t border-border bg-muted/30 px-3 py-2.5 space-y-1.5">
          {demo.whyItMatters && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Why this matters</p>
              <p className="text-xs text-foreground/90 mt-0.5 leading-snug">{demo.whyItMatters}</p>
            </div>
          )}
          {demo.bestFor && (
            <p className="text-xs text-muted-foreground leading-snug">
              <span className="font-medium text-foreground/80">Best for:</span> {demo.bestFor}
            </p>
          )}
          {demo.affects && demo.affects.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {demo.affects.map((a) => (
                <span key={a} className="inline-flex items-center rounded-full bg-background border border-border px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {a}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
